import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import ProductCard from '../components/product/ProductCard';
import { companyInfo } from '../data/mock';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RecommendationLanding = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [landingConfig, setLandingConfig] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        // Get landing config
        const configResponse = await axios.get(`${API_URL}/api/landing-configs/${id}`);
        setLandingConfig(configResponse.data);

        // Get products
        const productsResponse = await axios.get(`${API_URL}/api/products/`);
        const allProducts = productsResponse.data;
        
        // Match products by category from landing ID
        const categoryMap = {
          'landing_televisores': 'televisores',
          'landing_aires': 'aires',
          'landing_electrodomesticos': 'electrodomesticos',
          'landing_abanicos': 'abanicos',
          'landing_audio': 'audio'
        };
        
        const matchedCategory = categoryMap[id];
        const filteredProducts = matchedCategory 
          ? allProducts.filter(p => p.category === matchedCategory && p.available !== false)
          : allProducts.filter(p => p.available !== false).slice(0, 6);
        
        setProducts(filteredProducts);
      } catch (error) {
        console.error('Error loading landing:', error);
        navigate('/catalog');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  if (!landingConfig) {
    return null;
  }

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-DO', {
      style: 'currency',
      currency: 'DOP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section - Apple AirPods Max / Vision Pro Style */}
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
      >
        {/* Background Image with Blur */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${landingConfig.backgroundImage})`,
            backgroundPosition: 'center center',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            filter: 'blur(40px) brightness(0.7)',
            transform: 'scale(1.1)'
          }}
        />
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-background/90" />

        {/* Content Container */}
        <div className="relative z-10 container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 1 }}
            className="max-w-7xl mx-auto"
          >
            {/* Product Image - HUGE and Centered (Protagonist) */}
            <div className="mb-8">
              <img
                src={landingConfig.productImage}
                alt={landingConfig.title}
                className="w-full max-w-3xl mx-auto h-auto object-contain drop-shadow-2xl"
                style={{
                  maxHeight: '60vh',
                  filter: 'drop-shadow(0 40px 80px rgba(0,0,0,0.4))'
                }}
              />
            </div>

            {/* Title */}
            <h1
              className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-4"
              style={{
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif",
                fontWeight: 700,
                letterSpacing: '-0.04em',
                lineHeight: 1.1
              }}
            >
              {landingConfig.title}
            </h1>

            {/* Subtitle */}
            <p
              className="text-xl md:text-2xl lg:text-3xl text-white/90 mb-8 max-w-3xl mx-auto"
              style={{
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                fontWeight: 400,
                letterSpacing: '-0.01em',
                lineHeight: 1.4
              }}
            >
              {landingConfig.subtitle}
            </p>

            {/* Price Section */}
            <div className="mb-10">
              <div className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-2">
                {formatPrice(landingConfig.price)}
              </div>
              <div className="text-lg md:text-xl lg:text-2xl text-white/70 font-light">
                Desde {formatPrice(landingConfig.monthlyPrice)}/mes
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                size="lg"
                onClick={() => {
                  if (landingConfig.productId) {
                    navigate(`/product/${landingConfig.productId}`);
                  } else {
                    navigate('/catalog');
                  }
                }}
                className="px-10 py-7 text-lg rounded-full font-semibold bg-white text-black hover:bg-white/90 shadow-2xl min-w-[200px]"
              >
                Comprar
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => {
                  if (landingConfig.productId) {
                    navigate(`/product/${landingConfig.productId}`);
                  } else {
                    navigate('/catalog');
                  }
                }}
                className="px-10 py-7 text-lg rounded-full font-semibold border-2 border-white text-white hover:bg-white/10 backdrop-blur-sm min-w-[200px]"
              >
                Información
              </Button>
            </div>
          </motion.div>
        </div>

        {/* Bottom fade */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent" />
      </motion.section>

      {/* Products Section */}
      <section id="products-section" className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Explora toda la colección</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Descubre nuestra selección completa de {landingConfig.name.toLowerCase()}
            </p>
          </div>
          
          {products.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
              {products.map(product => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-muted-foreground text-lg mb-6">
                No hay productos disponibles en este momento
              </p>
              <Button size="lg" onClick={() => navigate('/catalog')} className="rounded-full px-8 py-6">
                Ver todo el catálogo
              </Button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default RecommendationLanding;
